import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import {
  FieldMappingForm,
  ImportPreviewTable,
  ImportStatusBadge,
  ImportStepIndicator,
  ImportSummaryCards,
} from "../../components/dealer/InventoryImportShared";
import { importStepFromStatus } from "../../constants/automapInventoryImportConstants";
import { useDealerPortalIntl } from "../../hooks/useDealerPortalIntl";
import { useLocale } from "../../intl";
import {
  dealerImportErrorsUrl,
  executeDealerImport,
  fetchDealerImport,
  trackImportEvent,
  updateDealerImportMapping,
  updateDealerImportRows,
} from "../../services/automapInventoryImportApi";

export default function DealerInventoryImportDetailPage() {
  const { importId } = useParams();
  const { dealerId } = useOutletContext();
  const navigate = useNavigate();
  const { t, translateError, formatImportMode, formatPublishMode, formatRowAction, templateFieldDefs } =
    useDealerPortalIntl();
  const { locale } = useLocale();
  const [importRecord, setImportRecord] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchDealerImport(dealerId, importId);
      setImportRecord(data.import);
      setMapping(data.import.fieldMapping || {});
      const firstRow = data.import.rows?.[0];
      if (firstRow?.rawData) setHeaders(Object.keys(firstRow.rawData));
    } catch (err) {
      setError(translateError(err.message, "loadImport"));
    } finally {
      setLoading(false);
    }
  }, [dealerId, importId, translateError]);

  useEffect(() => {
    load();
  }, [load]);

  const currentStep = importStepFromStatus(importRecord);
  const sampleRow = importRecord?.rows?.[0]?.rawData;

  const saveMapping = async () => {
    setBusy(true);
    setError("");
    try {
      trackImportEvent("inventory_mapping_completed", { importId });
      const data = await updateDealerImportMapping(dealerId, importId, mapping);
      setImportRecord(data.import);
      trackImportEvent("inventory_preview_generated", { importId });
    } catch (err) {
      setError(translateError(err.message, "loadImport"));
    } finally {
      setBusy(false);
    }
  };

  const handleRowAction = async (rowId, action) => {
    setImportRecord((prev) => ({
      ...prev,
      rows: prev.rows.map((row) => (row.id === rowId ? { ...row, action } : row)),
    }));
    if (action === "skip") trackImportEvent("inventory_import_row_skipped", { importId, rowId });
  };

  const saveRowActions = async () => {
    setBusy(true);
    try {
      const rows = importRecord.rows.map((row) => ({ rowId: row.id, action: row.action }));
      const data = await updateDealerImportRows(dealerId, importId, rows);
      setImportRecord(data.import);
    } catch (err) {
      setError(translateError(err.message, "loadImport"));
    } finally {
      setBusy(false);
    }
  };

  const runImport = async () => {
    setBusy(true);
    setError("");
    try {
      await saveRowActions();
      trackImportEvent("inventory_import_started", { importId });
      const data = await executeDealerImport(dealerId, importId);
      setImportRecord(data.import);
      trackImportEvent("inventory_import_completed", {
        importId,
        created: data.import.createdCount,
        updated: data.import.updatedCount,
      });
    } catch (err) {
      setError(translateError(err.message, "loadImport"));
      trackImportEvent("inventory_import_failed", { importId, message: err.message });
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="dealer-state-box">{t("import.loadingImport")}</p>;
  if (!importRecord) return <p className="dealer-state-box">{error || t("import.importNotFound")}</p>;

  return (
    <div className="import-page">
      <ImportStepIndicator currentStep={currentStep} />
      <div className="import-page-header">
        <div>
          <h2>{importRecord.sourceFilename}</h2>
          <p className="muted">
            <ImportStatusBadge status={importRecord.status} /> ·{" "}
            {new Date(importRecord.createdAt).toLocaleString(locale)}
          </p>
        </div>
        <div className="import-actions">
          <a className="dealer-portal-btn" href={dealerImportErrorsUrl(dealerId, importId)}>
            {t("import.downloadErrors")}
          </a>
          <Link className="dealer-portal-btn" to="/v/automap/dealer/inventario/importaciones">
            {t("import.history")}
          </Link>
        </div>
      </div>

      <ImportSummaryCards importRecord={importRecord} />

      <div className="dealer-state-box import-settings">
        <p>
          <strong>{t("import.modeLabel")}:</strong> {formatImportMode(importRecord.importMode)}
        </p>
        <p>
          <strong>{t("import.publishLabel")}:</strong> {formatPublishMode(importRecord.publishMode)}
        </p>
      </div>

      {error ? <p className="import-error">{error}</p> : null}

      {importRecord.status === "mapping_required" ? (
        <section className="dealer-state-box">
          <h3>{t("import.mapColumnsTitle")}</h3>
          <p className="muted">{t("import.mapColumnsDesc")}</p>
          <FieldMappingForm
            fields={templateFieldDefs}
            headers={headers}
            mapping={mapping}
            sampleRow={sampleRow}
            onChange={(key, value) => setMapping((prev) => ({ ...prev, [key]: value }))}
          />
          <div className="import-actions">
            <button type="button" className="dealer-portal-btn primary" disabled={busy} onClick={saveMapping}>
              {t("import.generatePreview")}
            </button>
          </div>
        </section>
      ) : null}

      {["preview_ready", "completed", "completed_with_errors", "importing"].includes(importRecord.status) ? (
        <section>
          <h3>{t("import.previewTitle")}</h3>
          <p className="muted">{t("import.previewDesc")}</p>
          <ImportPreviewTable
            rows={importRecord.rows}
            editable={importRecord.status === "preview_ready"}
            onActionChange={handleRowAction}
          />
          {importRecord.status === "preview_ready" ? (
            <div className="import-actions">
              <button type="button" className="dealer-portal-btn" disabled={busy} onClick={saveRowActions}>
                {t("import.saveActions")}
              </button>
              <button type="button" className="dealer-portal-btn primary" disabled={busy} onClick={runImport}>
                {busy ? t("import.importing") : t("import.confirmImport")}
              </button>
            </div>
          ) : null}
        </section>
      ) : null}

      {["completed", "completed_with_errors"].includes(importRecord.status) ? (
        <section className="dealer-state-box">
          <h3>{t("import.resultsTitle")}</h3>
          <p>
            {t("import.resultsSummary", {
              created: importRecord.createdCount,
              updated: importRecord.updatedCount,
              skipped: importRecord.skippedCount,
              errors: importRecord.errorCount,
            })}
          </p>
          <div className="import-actions">
            <Link className="dealer-portal-btn primary" to="/v/automap/dealer/inventario">
              {t("import.viewInventory")}
            </Link>
            <button
              type="button"
              className="dealer-portal-btn"
              onClick={() => navigate("/v/automap/dealer/inventario/importar")}
            >
              {t("import.newImport")}
            </button>
          </div>
          <ul className="import-result-links">
            {(importRecord.rows || [])
              .filter((row) => row.resultingListingId)
              .slice(0, 20)
              .map((row) => (
                <li key={row.id}>
                  <Link to={`/v/automap/dealer/inventario/${row.resultingListingId}`}>
                    {t("import.rowLink", { row: row.rowNumber, action: formatRowAction(row.action) })}
                  </Link>
                </li>
              ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
